import { Stack } from '@mui/material';
import { useMemo } from 'react';
import { ImportCompletedStep } from 'src/components/ImportPortalioProjectModal/import-completed-step';
import {
  IMPORT_MORE,
  usePortalioImport,
} from 'src/components/ImportPortalioProjectModal/portalio-import-context';
import { ImportPreviewList } from 'src/components/ImportProposalsModal/import-preview-list';
import { formatProjectCount } from 'src/components/ImportProposalsModal/import-results.utils';
import { ImportSummary } from 'src/components/ImportProposalsModal/import-summary';
import { ModalStep, useModalStep, useModalSteps } from 'src/components/ModalContainer';
import { usePortalioProjectImportPreview } from 'src/graphql/entities/integrations/portalio/hooks/use-portalio-project-import-preview';

export type ProjectsSummaryProps = {
  projects: { id: number; name: string }[];
};

/** Step 2: what importing the selected proposals would create and update. */
export const ProjectsSummary = ({ projects }: ProjectsSummaryProps) => {
  const { pushStep } = useModalSteps();
  const { popStep } = useModalStep();
  const { importProjects, importing, close } = usePortalioImport();
  const projectIds = projects.map((p) => p.id);
  const projectNames = useMemo(
    () => new Map(projects.map((p) => [String(p.id), p.name])),
    [projects],
  );
  const { records, loading, errors } = usePortalioProjectImportPreview(projectIds);
  const projectsLabel = formatProjectCount(projects.length);

  const handleImport = async () => {
    await importProjects(projectIds);
    const result = await pushStep(
      <ImportCompletedStep projectIds={projectIds} projectNames={projectNames} />,
    );
    if (result === IMPORT_MORE) {
      popStep(IMPORT_MORE);
    }
  };

  return (
    <ModalStep
      title="Review import"
      actions={[
        { label: 'Cancel', kind: 'secondary', onClick: close },
        {
          label: errors?.length ? 'Import anyway' : `Import ${projectsLabel}`,
          kind: 'primary',
          onClick: () => void handleImport(),
          disabled: projectIds.length === 0 || loading,
          loading: importing,
          loadingLabel: 'Importing',
          testId: 'import-portalio-projects-preview-import',
        },
      ]}
    >
      <Stack gap={1.5} data-testid="import-portalio-projects-preview">
        <ImportSummary
          status={loading ? `Checking ${projectsLabel}…` : `${projectsLabel} to import`}
          records={loading || !records?.length ? undefined : records}
          tense="preview"
          testId="import-portalio-projects-preview"
        />
        <ImportPreviewList
          records={records ?? []}
          errors={errors}
          projectNames={projectNames}
          loading={loading}
          testId="import-portalio-projects-preview"
        />
      </Stack>
    </ModalStep>
  );
};
